/**
 * Produsele al căror stoc îl punem cu mâna, în loc să-l citim din ExpertAccounts.
 *
 * Există fiindcă API-ul de stoc al EA poate lipsi cu totul: în septembrie 2026 `get_stock` a
 * răspuns cinci zile cu „Access denied, license expired", iar comenzile s-au luat după cifre
 * vechi de cinci zile, fără ca cineva să fie avertizat.
 *
 * Cifra din EA rămâne la vedere lângă cea pusă cu mâna. Fără ea nu s-ar ști cât de mult
 * diferă — și tocmai diferența spune dacă lista mai e necesară.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { SearchSelect } from '../ui/SearchSelect'

interface RandStocManual {
  productId: number
  sku: string
  name: string
  stocManual: number
  stocEa: number | null
  stocEaLaData: string | null
  pusDe: string | null
  pusLa: string | null
}

function eroare(e: unknown): string {
  const m = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message
  return m ?? 'A apărut o eroare.'
}

export function StocManualCard({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient()
  const [productId, setProductId] = useState<number | null>(null)
  const [cantitate, setCantitate] = useState('')

  const lista = useQuery<{ randuri: RandStocManual[] }>({
    queryKey: ['orders', 'stoc-manual'],
    queryFn: async () => (await api.get('/orders/stoc-manual')).data,
  })

  const produse = useQuery<{ products: { id: number; sku: string; name: string }[] }>({
    queryKey: ['sellout', 'products'],
    queryFn: async () => (await api.get('/sellout/products')).data,
  })

  const seteaza = useMutation({
    mutationFn: async (v: { productId: number; cantitate: number }) =>
      (await api.put('/orders/stoc-manual', v)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders', 'stoc-manual'] })
      setProductId(null)
      setCantitate('')
      toast.success('Stoc setat')
    },
    onError: (e: unknown) => toast.error(eroare(e)),
  })

  const scoate = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/orders/stoc-manual/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders', 'stoc-manual'] })
      toast.success('Scos din listă — stocul revine din EA')
    },
    onError: (e: unknown) => toast.error(eroare(e)),
  })

  const randuri = lista.data?.randuri ?? []
  // Cele deja în listă nu se mai propun: pentru ele se schimbă cifra direct în tabel.
  const optiuni = (produse.data?.products ?? [])
    .filter((p) => !randuri.some((r) => r.productId === p.id))
    .map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` }))

  return (
    <Card
      title="Stoc pus cu mâna"
      description="Pentru produsele din lista asta, comenzile folosesc cantitatea de aici, nu pe cea din ExpertAccounts."
    >
      {canWrite && (
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs text-b4l-text-muted">
            Produs
            <SearchSelect
              options={optiuni}
              value={productId}
              onChange={setProductId}
              placeholder="alege produsul…"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Cantitate
            <input
              value={cantitate}
              onChange={(e) => setCantitate(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0"
              className="w-28 rounded border border-b4l-border px-2 py-1 text-sm"
            />
          </label>
          <Button
            onClick={() =>
              seteaza.mutate({ productId: productId as number, cantitate: Number(cantitate) })
            }
            disabled={productId == null || cantitate === '' || seteaza.isPending}
          >
            Adaugă în listă
          </Button>
        </div>
      )}

      {lista.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
      {!lista.isLoading && randuri.length === 0 && (
        <p className="text-sm text-b4l-text-muted">
          Lista e goală — tot stocul vine din ExpertAccounts.
        </p>
      )}

      {randuri.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs text-b4l-text-muted">
                <th className="p-1.5">Produs</th>
                <th className="p-1.5 text-right">Stoc folosit</th>
                <th className="p-1.5 text-right">Ce spune EA</th>
                <th className="p-1.5">Pus de</th>
                <th className="p-1.5" />
              </tr>
            </thead>
            <tbody>
              {randuri.map((r) => (
                <tr key={r.productId} className="border-b border-b4l-border">
                  <td className="p-1.5">
                    {r.name}
                    <span className="ml-1.5 text-xs text-b4l-text-muted">{r.sku}</span>
                  </td>
                  <td className="p-1.5 text-right font-medium">
                    {canWrite ? (
                      <input
                        defaultValue={String(r.stocManual)}
                        onBlur={(e) => {
                          const v = Number(e.target.value)
                          if (Number.isFinite(v) && v >= 0 && v !== r.stocManual) {
                            seteaza.mutate({ productId: r.productId, cantitate: v })
                          }
                        }}
                        className="w-24 rounded border border-b4l-border px-2 py-0.5 text-right text-sm"
                      />
                    ) : (
                      r.stocManual
                    )}
                  </td>
                  <td className="p-1.5 text-right text-b4l-text-muted">
                    {r.stocEa ?? '—'}
                    {r.stocEaLaData && (
                      <span className="ml-1 text-[11px]">
                        ({new Date(r.stocEaLaData).toLocaleDateString('ro-RO')})
                      </span>
                    )}
                  </td>
                  <td className="p-1.5 text-xs text-b4l-text-muted">
                    {r.pusDe ?? '—'}
                    {r.pusLa && ` · ${new Date(r.pusLa).toLocaleDateString('ro-RO')}`}
                  </td>
                  <td className="p-1.5 text-right">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Scoți „${r.name}" din listă? Stocul va reveni din EA.`)) {
                            scoate.mutate(r.productId)
                          }
                        }}
                        className="text-xs text-b4l-red hover:underline"
                      >
                        Scoate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
